import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  getSelectedResourceIds,
  INQUIRY_SELECTION_EVENT,
} from "../utils/inquirySelection";
import { useAuth } from "../../context/AuthContext";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/resources", label: "Resources" },
  { to: "/skill-matrix", label: "Skill Matrix" },
  // { to: "/rate-card", label: "Rate Card" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);
  const { admin, token } = useAuth();
  const { pathname } = useLocation();
  const isAdminLoggedIn = Boolean(admin && token);

  useEffect(() => {
    const syncSelectedCount = () => {
      setSelectedCount(getSelectedResourceIds().length);
    };

    const onStorage = (event) => {
      if (event.key && event.key !== "resourcehub:selected-resource-ids") {
        return;
      }
      syncSelectedCount();
    };

    syncSelectedCount();
    window.addEventListener("storage", onStorage);
    window.addEventListener(INQUIRY_SELECTION_EVENT, syncSelectedCount);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(INQUIRY_SELECTION_EVENT, syncSelectedCount);
    };
  }, []);

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2 font-bold text-xl text-primary-700"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8"
              viewBox="0 0 32 32"
              fill="none"
            >
              <rect width="32" height="32" rx="6" fill="#2563eb" />
              <circle cx="16" cy="16" r="3.5" fill="white" />
              <circle cx="8" cy="8" r="2" fill="white" opacity="0.8" />
              <circle cx="24" cy="8" r="2" fill="white" opacity="0.8" />
              <circle cx="8" cy="24" r="2" fill="white" opacity="0.8" />
              <circle cx="24" cy="24" r="2" fill="white" opacity="0.8" />
              <line
                x1="12.5"
                y1="13"
                x2="9.5"
                y2="10"
                stroke="white"
                strokeWidth="1.5"
                opacity="0.6"
              />
              <line
                x1="19.5"
                y1="13"
                x2="22.5"
                y2="10"
                stroke="white"
                strokeWidth="1.5"
                opacity="0.6"
              />
              <line
                x1="12.5"
                y1="19"
                x2="9.5"
                y2="22"
                stroke="white"
                strokeWidth="1.5"
                opacity="0.6"
              />
              <line
                x1="19.5"
                y1="19"
                x2="22.5"
                y2="22"
                stroke="white"
                strokeWidth="1.5"
                opacity="0.6"
              />
            </svg>
            <span className="hidden sm:block">Smart Resource Hub</span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  pathname === link.to
                    ? "bg-primary-50 text-primary-700"
                    : "text-gray-600 hover:text-primary-700 hover:bg-gray-50"
                }`}
              >
                {link.label}
              </Link>
            ))}

            {selectedCount > 0 && (
              <Link
                to="/inquiry"
                className="ml-2 px-3 py-2 rounded-md text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors"
              >
                Inquiry ({selectedCount})
              </Link>
            )}

            {isAdminLoggedIn && (
              <Link
                to="/admin"
                className="ml-2 px-3 py-2 rounded-md text-sm font-medium border border-primary-200 text-primary-700 hover:bg-primary-50 transition-colors"
              >
                Dashboard
              </Link>
            )}
          </div>

          <button
            onClick={() => setOpen(!open)}
            className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            aria-label="Toggle menu"
          >
            {open ? (
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            ) : (
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            )}
          </button>
        </div>

        {open && (
          <div className="md:hidden pb-4 pt-2 border-t border-gray-100">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setOpen(false)}
                className={`block px-4 py-2.5 text-sm font-medium rounded-md mb-1 transition-colors ${
                  pathname === link.to
                    ? "bg-primary-50 text-primary-700"
                    : "text-gray-600 hover:text-primary-700 hover:bg-gray-50"
                }`}
              >
                {link.label}
              </Link>
            ))}

            {selectedCount > 0 && (
              <Link
                to="/inquiry"
                onClick={() => setOpen(false)}
                className="block px-4 py-2.5 text-sm font-medium rounded-md mb-1 transition-colors bg-primary-600 text-white hover:bg-primary-700"
              >
                Inquiry ({selectedCount})
              </Link>
            )}

            {isAdminLoggedIn && (
              <Link
                to="/admin"
                onClick={() => setOpen(false)}
                className="block px-4 py-2.5 text-sm font-medium rounded-md mb-1 transition-colors border border-primary-200 text-primary-700 hover:bg-primary-50"
              >
                Dashboard
              </Link>
            )}
          </div>
        )}

        {selectedCount > 0 && (
          <Link
            to="/inquiry"
            className="fixed bottom-6 right-6 z-40 btn-primary shadow-lg px-5 py-3 rounded-full"
          >
            Inquiry ({selectedCount})
          </Link>
        )}
      </div>
    </nav>
  );
}
